import { useState, useMemo } from "react";
import {
    TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, HelpCircle,
    ChevronDown, Info, Lightbulb,
} from "lucide-react";
import { cn } from "../../utils/tailwindUtils";
import { money, num } from "./adReportKit";

const VERDICT = {
    winner:   { label: "Working",       icon: TrendingUp,   pill: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300", edge: "border-l-emerald-500" },
    steady:   { label: "Holding",       icon: CheckCircle2, pill: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",             edge: "border-l-blue-400" },
    watch:    { label: "Slipping",      icon: AlertTriangle,pill: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",          edge: "border-l-amber-500" },
    loser:    { label: "Not working",   icon: TrendingDown, pill: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",                  edge: "border-l-red-500" },
    unproven: { label: "Too early",     icon: HelpCircle,   pill: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",             edge: "border-l-slate-400" },
};

const SEVERITY_MARK = {
    bad:  "text-red-600 dark:text-red-400",
    warn: "text-amber-600 dark:text-amber-400",
    good: "text-emerald-600 dark:text-emerald-400",
};

// "Active" leads and opens by default. A one-day view produces nothing but
// "too early to judge", so a panel that opened on "Needs action" showed an
// empty box and read as broken — while six ads were live and spending.
const FILTERS = [
    { key: "active",   label: "Active" },
    { key: "action",   label: "Needs action" },
    { key: "winner",   label: "Working" },
    { key: "all",      label: "Everything" },
];

const isLive = (s) => ["ACTIVE", "WITH_ISSUES"].includes((s || "").toUpperCase());

export default function AdDiagnosticsPanel({ diagnostics, benchmarks, caveats, diagWindow, currency = "₹" }) {
    const [filter, setFilter] = useState("active");
    const [openId, setOpenId] = useState(null);

    // Memoised because `diagnostics || []` is a new array every render when the
    // prop is absent, which would defeat both useMemos below.
    const rows = useMemo(() => diagnostics || [], [diagnostics]);

    const shown = useMemo(() => {
        if (filter === "active") return rows.filter(r => isLive(r.effective_status));
        if (filter === "winner") return rows.filter(r => r.verdict === "winner");
        if (filter === "all") return rows;
        return rows.filter(r => r.verdict === "loser" || r.verdict === "watch");
    }, [rows, filter]);

    const counts = useMemo(() => ({
        active: rows.filter(r => isLive(r.effective_status)).length,
        action: rows.filter(r => r.verdict === "loser" || r.verdict === "watch").length,
        winner: rows.filter(r => r.verdict === "winner").length,
        all: rows.length,
    }), [rows]);

    // Only money on live ads can still be saved; the rest is a lesson, not a fix.
    const recoverable = rows
        .filter(r => isLive(r.effective_status))
        .reduce((sum, r) => sum + (r.wasted_spend || 0), 0);
    const totalWasted = rows.reduce((sum, r) => sum + (r.wasted_spend || 0), 0);

    if (!rows.length) return null;

    return (
        <div className="rounded-xl border border-border bg-card mb-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 p-4 border-b border-border">
                <div>
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                        <Lightbulb className="h-4 w-4 text-amber-500" />
                        What is working, and why
                    </h3>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                        Each ad judged against this account&apos;s own medians —
                        {" "}CPL {money(benchmarks?.cpl)}, CTR {(benchmarks?.ctr || 0).toFixed(2)}%
                        {benchmarks?.sample ? ` across ${benchmarks.sample} ads` : ""}.
                        {/* Said plainly rather than hidden: on a short range these
                            verdicts cover more days than the table does, and a
                            reader comparing the two numbers deserves to know why. */}
                        {diagWindow?.widened && (
                            <> Verdicts read {diagWindow.days} days
                            ({diagWindow.from_date} – {diagWindow.to_date}), not just the
                            period above — a day or two of delivery is too little to judge.</>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-1 p-1 bg-muted rounded-lg w-fit">
                    {FILTERS.map(f => (
                        <button key={f.key} onClick={() => setFilter(f.key)}
                            className={cn("px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors",
                                filter === f.key
                                    ? "bg-background text-foreground shadow-sm ring-1 ring-border"
                                    : "text-muted-foreground hover:text-foreground")}>
                            {f.label}
                            <span className="ml-1.5 opacity-60">{counts[f.key]}</span>
                        </button>
                    ))}
                </div>
            </div>

            {totalWasted > 0 && (
                <div className="px-4 py-2.5 border-b border-border text-[11px] text-muted-foreground">
                    <strong className="text-foreground">{money(totalWasted)}</strong> was spent above
                    this account&apos;s median cost per lead in this period
                    {recoverable > 0
                        ? <> — <strong className="text-amber-700 dark:text-amber-400">{money(recoverable)}</strong> of it on ads that are still running, so that part is still recoverable.</>
                        : <> . All of it sits on ads that are already stopped, so it is a lesson for the next launch rather than a fix to make today.</>}
                </div>
            )}

            {(caveats || []).map((c, i) => (
                <div key={i} className="flex items-start gap-2 px-4 py-2.5 border-b border-border
                                        text-[11px] bg-amber-50/60 dark:bg-amber-900/10 text-amber-800 dark:text-amber-300">
                    <Info className="h-3.5 w-3.5 mt-px shrink-0" />
                    <span>{c}</span>
                </div>
            ))}

            <div className="divide-y divide-border">
                {shown.map(r => {
                    const v = VERDICT[r.verdict] || VERDICT.unproven;
                    const Icon = v.icon;
                    const open = openId === r.id;
                    const live = isLive(r.effective_status);
                    return (
                        <div key={r.id} className={cn("border-l-2", v.edge)}>
                            <button onClick={() => setOpenId(open ? null : r.id)}
                                className="w-full text-left px-4 py-3 hover:bg-accent/30 transition-colors">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium", v.pill)}>
                                                <Icon className="h-2.5 w-2.5" />
                                                {v.label}
                                            </span>
                                            <span className="text-xs font-medium text-foreground truncate max-w-[280px]">
                                                {r.name}
                                            </span>
                                            {!live && (
                                                <span className="text-[10px] text-muted-foreground">
                                                    {r.effective_status?.toLowerCase().replace(/_/g, " ")}
                                                </span>
                                            )}
                                        </div>
                                        {/* The single strongest reason, visible without expanding —
                                            the whole point is that the "why" is not hidden. */}
                                        {r.findings?.[0] && (
                                            <p className={cn("text-[11px] mt-1 line-clamp-2",
                                                SEVERITY_MARK[r.findings[0].severity])}>
                                                {r.findings[0].detail}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="text-xs font-semibold text-foreground tabular-nums">
                                            {money(r.spend)}
                                        </p>
                                        <p className="text-[10px] text-muted-foreground tabular-nums">
                                            {num(r.leads)} leads · {money(r.cpl)}/lead
                                        </p>
                                        {r.wasted_spend > 0 && (
                                            <p className="text-[10px] text-red-600 dark:text-red-400 tabular-nums">
                                                {money(r.wasted_spend)} over median
                                            </p>
                                        )}
                                    </div>
                                    <ChevronDown className={cn("h-4 w-4 text-muted-foreground shrink-0 transition-transform",
                                        open && "rotate-180")} />
                                </div>
                            </button>

                            {open && (
                                <div className="px-4 pb-4 space-y-3">
                                    <div className="space-y-2">
                                        {(r.findings || []).map((f, i) => (
                                            <div key={i} className="flex items-start gap-2">
                                                <span className={cn("text-[11px] font-bold leading-4 shrink-0 w-3",
                                                    SEVERITY_MARK[f.severity])}>
                                                    {f.severity === "good" ? "✓" : f.severity === "warn" ? "!" : "×"}
                                                </span>
                                                <div className="min-w-0">
                                                    <p className={cn("text-[11px] font-semibold", SEVERITY_MARK[f.severity])}>
                                                        {f.signal}
                                                    </p>
                                                    <p className="text-[11px] text-muted-foreground">{f.detail}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {(r.actions || []).length > 0 && (
                                        <div className="rounded-lg bg-muted/60 p-3">
                                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1.5">
                                                What to do
                                            </p>
                                            <ul className="space-y-1">
                                                {r.actions.map((a, i) => (
                                                    <li key={i} className="text-[11px] text-foreground flex gap-2">
                                                        <span className="text-amber-500">→</span>
                                                        <span>{a}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {r.campaign_name && (
                                        <p className="text-[10px] text-muted-foreground">
                                            Campaign: {r.campaign_name}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                {!shown.length && (
                    <p className="px-4 py-8 text-center text-xs text-muted-foreground">
                        {filter === "winner"
                            ? "No ad cleared the bar for a winner in this period — cheap cost per lead, and Meta rating it average or better on quality, engagement and conversion."
                            : filter === "active"
                                ? "Nothing is live in this selection. Switch the status filter to \"Paused only\" or \"Active & paused\" to see what ran earlier."
                                : "Nothing needs attention in this period."}
                    </p>
                )}
            </div>
        </div>
    );
}
